import { evaluateConditions } from '@bmo/form-core';

import type { WorkflowStep } from '@/api/workflows';

import type { FieldValue } from './FieldRenderer';

/** A group whose options can borrow a field or a section for inline display. */
export type RevealOwner = { id: string; type: string };

/**
 * Is one option of a reveal-owning group currently picked?
 *
 * A CheckboxGroup holds string[]; a RadioGroup holds a scalar. The null/empty
 * guard matters on the radio side: an unanswered group is undefined, which must
 * not match an option whose value is ''.
 *
 * The single answer to "is this reveal open?", so every caller agrees. Get the
 * edges wrong (a radio value of '', a checkbox holding a non-array) and a field
 * is required but invisible, or printed while off screen.
 */
export function isRevealOptionSelected(
  owner: RevealOwner,
  values: Record<string, FieldValue>,
  optionValue: string,
): boolean {
  const raw = values[owner.id];
  return owner.type === 'RadioGroup'
    ? raw !== undefined && raw !== null && raw !== '' && String(raw) === optionValue
    : ((raw as string[] | undefined) ?? []).includes(optionValue);
}

/**
 * Fields borrowed by a RadioGroup / CheckboxGroup option (`revealFieldId`, or
 * every field inside a section borrowed via `revealSectionId`) that is NOT
 * currently revealed — because the option isn't selected, or its owning group
 * is itself hidden.
 *
 * These render nowhere, so they must not count toward required-completeness,
 * and must not appear on the Application Review either.
 *
 * ## This is what lets a closed reveal KEEP its answers (BMO #304/#305)
 *
 * Closing a reveal does not empty it: re-ticking the option brings back what the
 * advisor entered, which is spec §6.4's "hidden values are preserved while the
 * form is open" applied to reveals. Closing one used to wipe it unconditionally,
 * and no setting could switch that off — a reveal target carries no `conditions`
 * of its own (its visibility belongs to the owning option), so the
 * `clearHiddenAnswers` opt-in, which only clears what a field's OWN conditions
 * hide, was structurally unable to reach it in either position.
 *
 * Retaining is safe only because this function is honoured on every path that
 * leaves the browser: `visibleValues` (PDF fill, per repeatable instance),
 * `buildApplicationSnapshot` (Application Review and the BMO payload), and the
 * completeness walkers. A new consumer of the raw values map that skips it would
 * print or submit a withdrawn answer — that is the obligation this carries now.
 * NOT yet honoured by `fieldVisibleForUniqueness`, which judges visibility from
 * conditions alone; latent today because no reveal target in any shipped form
 * carries a `uniquenessGroup`.
 * They can't be skipped by the ordinary field-condition check the way a hidden
 * section's fields are: a reveal target deliberately carries no conditions of
 * its own (the section walker bypasses them for borrowed fields), because its
 * visibility belongs to the owning option. Without this, ticking "No
 * contribution or transfer at this time" still blocked Next on the hidden
 * "Account number" the "Internal transfer" card would have revealed.
 */
export function hiddenBorrowedFieldIds(
  step: WorkflowStep,
  values: Record<string, FieldValue>,
): Set<string> {
  const hidden = new Set<string>();
  if (step.type !== 'DynamicForm') return hidden;
  // Section reveals (`revealSectionId`) hide a whole BLOCK, so the ids are
  // resolved through this index rather than read off the option. Built up front
  // because an option may borrow a section declared later in the step.
  const fieldIdsBySection = new Map<string, string[]>();
  for (const section of step.sections) {
    const ids: string[] = [];
    for (const row of section.rows) {
      for (const col of row.columns) {
        if (col.field) ids.push(col.field.id);
      }
    }
    fieldIdsBySection.set(section.id, ids);
  }
  for (const section of step.sections) {
    const sectionVisible =
      (section.conditions?.length ?? 0) === 0 ||
      evaluateConditions(section.conditions, section.conditionCombinator, values);
    for (const row of section.rows) {
      for (const col of row.columns) {
        const owner = col.field;
        if (!owner || (owner.type !== 'CheckboxGroup' && owner.type !== 'RadioGroup')) continue;
        const ownerVisible =
          sectionVisible &&
          ((owner.conditions?.length ?? 0) === 0 ||
            evaluateConditions(owner.conditions, owner.conditionCombinator, values));
        // Bare field id (inside `isRevealOptionSelected`): reveals inside a
        // REPEATABLE section aren't supported (the borrowed-field lookup is by
        // bare id too), so there is no instance-qualified key to read here. If
        // one is ever authored there this resolves to "not selected", which
        // fails OPEN — the reveal simply isn't treated as required — rather
        // than blocking Next on a field the advisor can't see.
        for (const option of owner.options ?? []) {
          if (ownerVisible && isRevealOptionSelected(owner, values, option.value)) continue;
          if (option.revealFieldId) hidden.add(option.revealFieldId);
          // A borrowed SECTION hides every field it holds. Without this, ticking
          // "Internal transfer" alone would still block Next on the eight
          // required fields inside the un-revealed "Incoming transfer" block —
          // fields the advisor has no way to reach — and Application Review
          // would list them too.
          if (option.revealSectionId) {
            for (const id of fieldIdsBySection.get(option.revealSectionId) ?? []) {
              hidden.add(id);
            }
          }
        }
      }
    }
  }
  return hidden;
}

// Exported for tests — the reveal/required interaction below is easy to
// regress and hard to reach through a full FormShell render.
